import { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n';

const LanguageContext = createContext();

const translations = {
  en: () => import('../locales/en.json'),
  am: () => import('../locales/am.json'),
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });
  const [translationsData, setTranslationsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTranslations = async () => {
      setLoading(true);
      try {
        const module = await translations[language]();
        // JSON imports in Vite return the data directly as default
        const loadedData = module.default || module;
        setTranslationsData(loadedData);
        // console.log("Loaded translations data:", loadedData); // Temporary log
      } catch (error) {
        // console.error('Error loading translations:', error);
        // Fallback to English if translation fails
        try {
          const enModule = await translations.en();
          setTranslationsData(enModule.default || enModule);
        } catch (fallbackError) {
          // console.error('Error loading fallback translations:', fallbackError);
          setTranslationsData({});
        }
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
  }, [language]);

  useEffect(() => {
    // Keep react-i18next in sync with LanguageContext selection
    try {
      i18n.changeLanguage(language);
    } catch (e) {
      // console.error('Failed to sync i18n language:', e);
    }
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    // Also update the i18next instance so components using useTranslation get updated
    try {
      i18n.changeLanguage(lang);
    } catch (e) {
      // console.error('Failed to change i18n language:', e);
    }
  };

  const t = (path, params = {}) => {
    if (!translationsData) return path;
    
    const keys = path.split('.');
    let value = translationsData;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return path; // Return path if translation not found
      }
    }
    
    // If value is a string, interpolate parameters
    if (typeof value === 'string') {
      let interpolated = value;
      Object.keys(params).forEach(key => {
        const regex = new RegExp(`\\{${key}\\}`, 'g');
        interpolated = interpolated.replace(regex, params[key]);
      });
      return interpolated;
    }
    
    // Return the value if it's an array or other valid type
    if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
      return value;
    }
    
    return path; // Return path if value is not a valid type
  };

  const value = {
    language,
    changeLanguage,
    t,
    loading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

